/**
 * Connected state for action cards: participation, pending requests, display-on-profile prompt,
 * and targeted issues.
 */
import type { Dispatch, MouseEvent, SetStateAction } from "react";
import { useEffect, useState } from "react";
import api from "config/axiosConfig";
import { toast } from "sonner";
import type { Action } from "util/constants";
import { useUser } from "util/UserContext";
import type { PrivacyLevel } from "util/userUtils";
import { apiRoutes } from "util/apiRoutes";

export interface ActionTargetSummary {
  id: number;
}

interface UseActionCardStateResult {
  currentUser: ReturnType<typeof useUser>["currentUser"];
  isParticipant: boolean;
  hasPendingRequest: boolean;
  targets: ActionTargetSummary[];
  setTargets: Dispatch<SetStateAction<ActionTargetSummary[]>>;
  targetsOpen: boolean;
  setTargetsOpen: Dispatch<SetStateAction<boolean>>;
  profileOpen: boolean;
  setProfileOpen: Dispatch<SetStateAction<boolean>>;
  selectedPrivacy: PrivacyLevel;
  handleJoinRequest: (event: MouseEvent) => Promise<void>;
  handleCancelParticipation: (event: MouseEvent) => Promise<void>;
  handleCancelRequest: (event: MouseEvent) => Promise<void>;
}

/**
 * Loads action-specific relationship state while keeping the card component mostly declarative.
 */
export function useActionCardState(action: Action): UseActionCardStateResult {
  const { currentUser, setShowAuthModal } = useUser();
  const [isParticipant, setIsParticipant] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [targets, setTargets] = useState<ActionTargetSummary[]>([]);
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedPrivacy, setSelectedPrivacy] = useState<PrivacyLevel>(
    currentUser?.properties?.privacy_content_default_audience?.value || "public"
  );

  useEffect(() => {
    setSelectedPrivacy(currentUser?.properties?.privacy_content_default_audience?.value || "public");
  }, [currentUser]);

  useEffect(() => {
    const loadParticipationState = async () => {
      if (!action?.id) {
        return;
      }

      if (!currentUser) {
        setIsParticipant(false);
        setHasPendingRequest(false);
        return;
      }

      try {
        const statusResponse = await api.get(apiRoutes.posts.participants.status(action.id));
        setIsParticipant(Boolean(statusResponse.data?.isParticipant));
      } catch {
        setIsParticipant(false);
      }

      try {
        const requestResponse = await api.get(apiRoutes.requests.root, {
          params: {
            requestorId: currentUser.id,
            requestorType: "user",
            entityType: "action",
            entityId: action.id,
            type: "action_join",
            status: "pending",
          },
        });
        const pendingRequest = requestResponse.data.requests?.[0];
        setHasPendingRequest(
          Boolean(requestResponse.data.requests?.length) &&
            pendingRequest?.request_type === "action_join"
        );
      } catch {
        setHasPendingRequest(false);
      }
    };

    void loadParticipationState();
  }, [action?.id, currentUser]);

  useEffect(() => {
    const loadTargets = async () => {
      if (!action?.id) {
        return;
      }

      try {
        const response = await api.get(apiRoutes.entities.relations, {
          params: {
            user_id: 0,
            node_id: action.id,
            node_type: "action",
            rel_type: "target",
            is_active: true,
          },
        });

        const relations = response.data as Array<{ related_id: number; related_type: string }>;
        const issueTargets = relations.filter((relation) => relation.related_type === "issue");
        setTargets(issueTargets.map((relation) => ({ id: relation.related_id })));
      } catch {
        setTargets([]);
      }
    };

    void loadTargets();
  }, [action?.id]);

  const handleJoinRequest = async (event: MouseEvent): Promise<void> => {
    event.stopPropagation();
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    try {
      const response = await api.post(apiRoutes.posts.participants.list(action.id));
      if (response.data.auto_joined) {
        setIsParticipant(true);
        const isPrivate = Boolean(action.properties?.is_private);
        toast.success("Joined action", {
          action: !isPrivate
            ? {
                label: "Highlight on profile",
                onClick: () => setProfileOpen(true),
              }
            : undefined,
          duration: 3000,
        });
        return;
      }

      setHasPendingRequest(true);
      toast.info("Request sent to join private action");
    } catch {
      toast.error("Could not join this action.");
    }
  };

  const handleCancelParticipation = async (event: MouseEvent): Promise<void> => {
    event.stopPropagation();
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    try {
      await api.delete(apiRoutes.posts.participants.byUser(action.id, currentUser.id));
      setIsParticipant(false);
      setHasPendingRequest(false);
      toast("You left the action");
    } catch {
      toast.error("Could not leave this action.");
    }
  };

  const handleCancelRequest = async (event: MouseEvent): Promise<void> => {
    event.stopPropagation();
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    try {
      const response = await api.get(apiRoutes.requests.root, {
        params: {
          requestorId: currentUser.id,
          requestorType: "user",
          entityType: "action",
          entityId: action.id,
          type: "action_join",
          status: "pending",
        },
      });

      const requestId = response.data.requests?.[0]?.id;
      if (!requestId) {
        return;
      }

      await api.delete(apiRoutes.requests.cancel(requestId));
      setIsParticipant(false);
      setHasPendingRequest(false);
    } catch {
      toast.error("Could not cancel the join request.");
    }
  };

  return {
    currentUser,
    isParticipant,
    hasPendingRequest,
    targets,
    setTargets,
    targetsOpen,
    setTargetsOpen,
    profileOpen,
    setProfileOpen,
    selectedPrivacy,
    handleJoinRequest,
    handleCancelParticipation,
    handleCancelRequest,
  };
}
